import './Broadcast.sass';

import React, { FC, useCallback, useEffect, useMemo } from 'react';
import Select from 'react-select';

import { Recipient } from './Recipient/Recipient';
import { Loader, MetaTags } from '$shared/components';
import {
  BottomBar,
  Button,
  Form,
  FormItem,
  Header,
  InlineHeader,
  Placeholder,
  ScrollViewVirtualized,
  SearchInput,
} from '$uikit';
import { IcChart28, IcDropdown16, IcFilters28 } from '$assets';
import { imActions, imBroadcastReceiversCountSelector, imSelector } from '$store/im';
import { useTranslator } from '$hooks';
import { useDispatch, useSelector } from 'react-redux';
import { goTo } from '$navigation/helper';
import { BROADCAST_MESSAGE, BROADCAST_STATS, IM } from '$shared/constants/pages';
import { toastActions } from '$store/toast';
import { mainActions, mainSelector } from '$store/main';
import { classNames as cn } from '$utils';
import { BroadcastFiltersBottomSheet } from '$core/Broadcast/BroadcastFiltersBottomSheet/BroadcastFiltersBottomSheet';

export const Broadcast: FC = () => {
  const t = useTranslator();
  const { canBroadcastMessages, mVerified, users } = useSelector(mainSelector);
  const receiversCount = useSelector(imBroadcastReceiversCountSelector);
  const { broadcast } = useSelector(imSelector);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!broadcast.recipientIds.length) {
      dispatch(imActions.loadBroadcastLists());
    }
  }, [dispatch]);

  useEffect(() => {
    if (!mVerified) {
      goTo(IM);
    }
  }, [mVerified]);

  const hasFilters = useMemo(
    () => Object.values(broadcast.appliedActiveFilters).some(Boolean),
    [broadcast.appliedActiveFilters],
  );

  const options = useMemo(
    () =>
      broadcast.lists.map((list) => ({
        value: list.listId,
        label: list.title,
        amount: list.count,
      })),
    [broadcast.lists, t],
  );

  const handleSendToChange = useCallback(
    (options) => {
      dispatch(imActions.setBroadcastSendTo(options.map((o) => o.value)));
    },
    [dispatch],
  );

  const handleExcludeChange = useCallback(
    (options) => {
      dispatch(imActions.setBroadcastExclude(options.map((o) => o.value)));
    },
    [dispatch],
  );

  const handleSearch = useCallback(
    (value: string) => {
      dispatch(imActions.setBroadcastSearchText(value));
    },
    [dispatch],
  );

  const handleResetSearch = useCallback(() => {
    dispatch(imActions.resetBroadcastSearchText());
  }, [dispatch]);

  const value = useMemo(
    () => options.filter((o) => broadcast.sendTo.includes(o.value)),
    [options, broadcast.sendTo],
  );

  const exclude = useMemo(
    () => options.filter((o) => broadcast.exclude.includes(o.value)),
    [options, broadcast.exclude],
  );

  const formatOptionLabel = (option) => (
    <div className="Broadcast__select__option">
      {option.label}
      <span className="Broadcast__Select__option__amount">{option.amount}</span>
    </div>
  );

  const handleClickNext = useCallback(() => {
    if (broadcast.recipientIds.length) {
      goTo(BROADCAST_MESSAGE);
    }
  }, [broadcast]);

  const handleRecipientClick = useCallback((id, selected, skip) => {
    if (skip) {
      dispatch(toastActions.setToastFail(t('broadcast_select_error')));
    } else {
      dispatch(imActions.setBroadcastExcludeUserId({ id, value: !selected }));
    }
  }, []);

  const handleOpenFilters = useCallback(() => {
    dispatch(mainActions.setPopup(<BroadcastFiltersBottomSheet />));
  }, []);

  const renderForm = () => {
    return (
      <>
        <div id="broadcast-form">
          <div className="Broadcast__caption">{t('broadcast_caption')}</div>
          <Form className="Broadcast">
            <InlineHeader counter="">{t('broadcast_send_to_title')}</InlineHeader>
            <FormItem>
              <Select
                isDisabled={broadcast.isListsLoading}
                formatOptionLabel={formatOptionLabel}
                classNamePrefix="Select"
                isMulti
                value={value}
                onChange={handleSendToChange}
                options={options.filter((o) => !broadcast.exclude.includes(o.value))}
                placeholder={t('broadcast_send_to_select_placeholder')}
                noOptionsMessage={() => t('edit_countries_not_found')}
                components={{
                  DropdownIndicator: () => <IcDropdown16 />,
                }}
              />
            </FormItem>
            <InlineHeader>{t('broadcast_exclude_title')}</InlineHeader>
            <FormItem>
              <Select
                isDisabled={broadcast.isListsLoading}
                formatOptionLabel={formatOptionLabel}
                classNamePrefix="Select"
                isMulti
                value={exclude}
                onChange={handleExcludeChange}
                options={options.filter((o) => !broadcast.sendTo.includes(o.value))}
                placeholder={t('broadcast_send_exclude_placeholder')}
                noOptionsMessage={() => t('edit_countries_not_found')}
                components={{
                  DropdownIndicator: () => <IcDropdown16 />,
                }}
              />
            </FormItem>
            <InlineHeader>
              {t('broadcast_recipients_title')}
              <span className="Broadcast__header__buttons">
                <div
                  onClick={handleOpenFilters}
                  className={cn('Broadcast__header__button', {
                    active: hasFilters,
                  })}
                >
                  <IcFilters28 />
                </div>
                {/*<div className={cn("Broadcast__header__button", {*/}
                {/*  active: false*/}
                {/*})}>*/}
                {/*  <IcSort28 />*/}
                {/*</div>*/}
              </span>
            </InlineHeader>
            <FormItem>
              <SearchInput
                disabled={broadcast.isListsLoading}
                placeholder={t('broadcast_recipients_search_placeholder')}
                query={broadcast.searchText}
                onChange={handleSearch}
              />
            </FormItem>
          </Form>
        </div>
      </>
    );
  };

  const renderEmptyPlaceholder = () => {
    return (
      <>
        {broadcast.isRecipientsLoading ? (
          <Loader className="Broadcast__emptyLoader" />
        ) : (
          !broadcast.userIds.length && (
            <Placeholder
              actions={
                (!!broadcast.searchText.length || hasFilters) && (
                  <Button
                    onClick={
                      broadcast.searchText.length ? handleResetSearch : handleOpenFilters
                    }
                    stretched
                    size="small"
                    mode="secondary"
                  >
                    {t(
                      broadcast.searchText.length
                        ? 'broadcast_recipients_search_reset_button'
                        : 'broadcast_recipients_open_filters_button',
                    )}
                  </Button>
                )
              }
              centered
            >
              {t('broadcast_recipients_empty_placeholder')}
            </Placeholder>
          )
        )}
      </>
    );
  };

  if (!canBroadcastMessages) {
    goTo(IM);
    return null;
  }

  const isEmpty = !broadcast.userIds.length || broadcast.isRecipientsLoading;

  return (
    <>
      <MetaTags title={t('broadcast_title')} />
      <Header
        showBackButton
        rightIcons={[
          {
            icon: <IcChart28 />,
            onClick: () => goTo(BROADCAST_STATS),
          },
        ]}
      >
        <div className="Broadcast__headerWrap">
          <div className="Broadcast__headerWrap__title">{t('broadcast_title')}</div>
          <div className="Broadcast__headerWrap__subtitle">
            {broadcast.isListsLoading || broadcast.isRecipientsLoading
              ? t('loading')
              : t('broadcast_subtitle', { count: receiversCount })}
          </div>
        </div>
      </Header>
      {broadcast.isListsLoading ? (
        <Loader isWrapped />
      ) : (
        <>
          <div className="Broadcast__form-container">{renderForm()}</div>
          <ScrollViewVirtualized
            className={cn('Broadcast__recipientsList', {
              empty: isEmpty,
            })}
            rowCount={isEmpty ? 1 : broadcast.userIds.length}
            rowHeight={48}
            renderRow={(index) => {
              if (index === 0 && isEmpty) {
                return renderEmptyPlaceholder();
              }
              const userId = broadcast.userIds[index];
              const user = users[userId];
              const skip = broadcast.skipIds.includes(userId);
              const exclude = broadcast.excludeUserIds.includes(userId);

              return (
                <Recipient
                  key={user.id}
                  disabled={skip}
                  onSelect={(selected) => handleRecipientClick(user.id, selected, skip)}
                  selected={!(skip || exclude)}
                  userId={user.id}
                />
              );
            }}
          />
        </>
      )}
      <BottomBar>
        <Button onClick={handleClickNext} stretched disabled={!receiversCount}>
          {t('next_button')}
        </Button>
      </BottomBar>
    </>
  );
};
